import { useMemo } from "react";
import { useQueryData, useLinks } from "hooks";
import { GET_MISSION_ROLE_APPLICANTS_SHARE_DATA } from "graphql/queries";
import { formatCurrency, getUrlFromLinks } from "utils";
import { EXTERNAL_LINK_VALUES, RATE_FREQUENCIES } from "constants/index";

/**
 * useShareApplicants
 *
 * @param {Array} applicantIds
 */
export default function useShareApplicants({ applicantIds = [] }) {
  const links = useLinks();

  const { loading, error, data } = useQueryData({
    queryName: GET_MISSION_ROLE_APPLICANTS_SHARE_DATA,
    variables: {
      filters: {
        ids: applicantIds,
      },
    },
    skip: applicantIds.length === 0,
  });

  const textualData = useMemo(() => {
    if (!data?.missionRoleApplicants?.nodes?.length) return "";

    return data.missionRoleApplicants.nodes
      .map((applicant) => {
        const parts = [];

        // Link to the Application
        const appLink = `${location.origin}${links.applicant_preview(applicant.uuid)}`;
        const pin = applicant.missionRole.mission.organization.applicationsPin;
        parts.push(`${appLink}${pin ? ` (PIN: ${pin})` : ""}`);

        // Name, Country, Rate
        const name = applicant.profile.name;
        const country = applicant.profile?.location?.label || "";
        let rate = "";
        if (applicant.negotiatedSellingRate?.amount) {
          const rateValue = formatCurrency(
            applicant.negotiatedSellingRate.amount,
            applicant.negotiatedSellingRate.currency
          );
          const freq = applicant.negotiatedSellingRate.frequency === RATE_FREQUENCIES.hourly ? "/ h" : "/ mo";
          rate = `${rateValue} ${freq}`;
        }
        parts.push(`${name}, ${country}${rate ? ` — ${rate}` : ""}`);

        // LinkedIn
        const linkedin = getUrlFromLinks(applicant.profile.externalLinks, EXTERNAL_LINK_VALUES.LinkedIn);
        if (linkedin) parts.push(`LinkedIn: ${linkedin}`);

        // Role Applied To
        const role = applicant.missionRole.name;
        const mission = applicant.missionRole.mission.name;
        parts.push(`Applied to: ${role} at ${mission}`);

        // Disciplines and Skills
        const disciplines = applicant.profile.disciplines?.map((d) => d.name).join(", ");
        const skills = applicant.profile.skills?.map((s) => s.name).join(", ");
        if (disciplines || skills) parts.push(`Disciplines: ${disciplines || "—"}\nSkills: ${skills || "—"}`);

        return parts.join("\n");
      })
      .join("\n\n");
  }, [data, links]);

  return {
    loading,
    data: data?.missionRoleApplicants?.nodes,
    error,
    textualData,
  };
}
